/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import type { ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import type {
	ActionRendererFor,
	MessageActionContentRenderer,
} from '@atlassian/conversation-assistant-message-actions-api/types';

import { ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import messages from '../ask-question/messages';

type AskQuestionQuestions = ActionRendererFor<
	typeof ActionKeys.AskQuestion
>['actions'][number]['data']['questions'];

type OptionAnswer = {
	type: 'option';
	optionIndex: number;
};

type CustomAnswer = {
	type: 'custom';
	text: string;
};

type AutoAnswer = {
	type: 'auto';
};

type QuestionAnswer = OptionAnswer | CustomAnswer | AutoAnswer;

export type AskQuestionCompletedViewAnswerState = Record<number, QuestionAnswer | undefined>;

const styles = cssMap({
	completedItemRow: {
		paddingTop: token('space.075'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.large'),
	},
	completedNumberContainer: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'center',
		minHeight: '24px',
		width: '20px',
		paddingLeft: token('space.025'),
		paddingRight: token('space.025'),
		paddingTop: token('space.025'),
		flexShrink: 0,
	},
	completedAnswerBlock: {
		paddingRight: token('space.100'),
		paddingBottom: token('space.075'),
		borderRadius: token('radius.large'),
	},
	rightAlignedAnswersBubble: {
		width: '80%',
		marginInlineStart: 'auto',
	},
});

export type AskQuestionCompletedViewProps = {
	questions: AskQuestionQuestions;
	answers: AskQuestionCompletedViewAnswerState;
	ContentRenderer: MessageActionContentRenderer;
	productKey: string;
	cloudId: string;
	conversationId?: string;
	invocationId?: string;
	headerIcon?: React.ReactNode;
};

export const AskQuestionCompletedView = ({
	questions,
	answers,
	ContentRenderer,
	productKey,
	cloudId,
	conversationId,
	invocationId,
	headerIcon,
}: AskQuestionCompletedViewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	// Fire once per mount — this component only renders when the answer card is shown.
	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'ui',
			action: 'viewed',
			actionSubject: 'AskQuestionCompletedView',
			actionSubjectId: 'answer',
			attributes: {
				conversationId: conversationId ?? '',
				invocationId: invocationId ?? '',
				questionsCount: questions.length,
				answersCount: Object.keys(answers).length,
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [fallbackIndices] = useState(() =>
		questions.map((q) => Math.floor(Math.random() * q.options.length)),
	);

	const displayAnswers = questions.map((q, idx) => {
		const ans = answers[idx];
		if (ans?.type === 'option') {
			const option = q.options[ans.optionIndex];
			return {
				label: option?.label ?? '',
				description: option?.description ?? '',
			};
		}
		if (ans?.type === 'custom' && ans.text.trim()) {
			return { label: formatMessage(messages.customAnswerLabel), description: ans.text };
		}
		if (ans?.type === 'auto') {
			return {
				label: formatMessage(messages.skippedAnswerLabel),
				description: formatMessage(messages.autoAnswerLabel),
			};
		}
		const option = q.options[fallbackIndices[idx]] ?? q.options[0];
		return {
			label: option?.label ?? '',
			description: option?.description ?? '',
		};
	});

	return (
		<Box xcss={styles.rightAlignedAnswersBubble}>
			<ActionCardWrapper
				title={formatMessage(messages.completedTitle)}
				subtitle={formatMessage(messages.completedSubtitle, { count: questions.length })}
				product="ask-question"
				isExpandable
				defaultExpanded
				isTitleBold
				invertChevron
				headerIcon={headerIcon}
			>
				<Stack space="space.050">
					{displayAnswers.map((answer, idx) => (
						<Box key={idx} xcss={styles.completedItemRow}>
							<Inline space="space.200" alignBlock="start" shouldWrap={false}>
								<Box xcss={styles.completedNumberContainer}>
									<Text weight="medium">{idx + 1}.</Text>
								</Box>
								<Stack space="space.050">
									<ContentRenderer
										content={questions[idx].question}
										productKey={productKey}
										siteId={cloudId}
										boldText
									/>
									<Box xcss={styles.completedAnswerBlock}>
										<Stack>
											<Text weight="medium">{answer.label}</Text>
											{answer.description && (
												<Text color="color.text.subtle">{answer.description}</Text>
											)}
										</Stack>
									</Box>
								</Stack>
							</Inline>
						</Box>
					))}
				</Stack>
			</ActionCardWrapper>
		</Box>
	);
};
